import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useSeoOverride } from "@/components/seo/SeoContext";

/**
 * RouteSeo — per-route SEO for the public Rota detail page.
 *
 * It does NOT render title/description/OG itself (that caused DUPLICATE
 * meta tags with the global <SeoHead> under react-helmet-async@3.0.0).
 * Instead it PUSHES the route's title/description/robots up through
 * SeoContext, so <SeoHead> emits a single, route-specific instance of
 * each. RouteSeo only renders the JSON-LD TouristTrip (single source,
 * no dedupe concern) directly via Helmet.
 */

const SITE_NAME = "MotoTrips";
const BASE = "https://mototrips.net";
const SUPPORTED = ["pt", "en", "es", "fr"];

function normLng(raw) {
  const l = String(raw || "pt").slice(0, 2).toLowerCase();
  return SUPPORTED.includes(l) ? l : "pt";
}

function buildDescription(lng, locText, distKm, owner) {
  const dist = Math.round(Number(distKm) || 0);
  const by = owner ? ` ${owner}` : "";
  const t = {
    pt: `Rota de mota${locText ? " por " + locText : ""} — ${dist} km. Partilhada por${by} no ${SITE_NAME}.`,
    en: `Motorcycle route${locText ? " through " + locText : ""} — ${dist} km. Shared by${by} on ${SITE_NAME}.`,
    es: `Ruta en moto${locText ? " por " + locText : ""} — ${dist} km. Compartida por${by} en ${SITE_NAME}.`,
    fr: `Itinéraire moto${locText ? " à travers " + locText : ""} — ${dist} km. Partagé par${by} sur ${SITE_NAME}.`,
  };
  return (t[lng] || t.pt).slice(0, 200);
}

export const RouteSeo = ({ route }) => {
  const { i18n } = useTranslation();
  const lng = normLng(i18n.language);
  const hasRoute = !!route?.route_id;

  const name = route?.name || "Rota";
  const startLoc = route?.start_locality_global || "";
  const endLoc = route?.end_locality_global || "";
  const locText = [startLoc, endLoc].filter(Boolean).join(" → ");
  const owner = route?.author?.name || "";
  const title = `${name} - ${SITE_NAME}`;
  const description = buildDescription(lng, locText, route?.total_distance_km, owner);
  const isUnlisted = route?.visibility === "unlisted";

  // Push the route's title/description/robots so the GLOBAL SeoHead emits
  // a SINGLE instance of each (no duplicate meta). null when no route.
  useSeoOverride(
    hasRoute
      ? { title, description, robots: isUnlisted ? "noindex,follow" : null }
      : null,
  );

  if (!hasRoute) return null;

  // JSON-LD TouristTrip — strong "this is a real content page" signal for
  // Google (mirrors the backend bot-prerender). Single source via Helmet.
  const canonicalUrl = typeof window !== "undefined"
    ? `${BASE}${window.location.pathname}`
    : `${BASE}/rota/${route.route_id}`;
  const dist = Math.round(Number(route.total_distance_km) || 0);
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "TouristTrip",
    name,
    description,
    url: canonicalUrl,
    inLanguage: lng,
    touristType: "Motorcycle touring",
  };
  if (owner) jsonLd.author = { "@type": "Person", name: owner };
  if (dist > 0) {
    jsonLd.additionalProperty = [
      { "@type": "PropertyValue", name: "distance_km", value: dist },
    ];
  }

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );
};

export default RouteSeo;
